import { Icon, Loader } from "@screencloud/screencloud-ui-components";
import { useCallback, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useHistory } from "react-router";
import { StepProgress } from "src/components/StepProgress";
import { WizardModal } from "src/components/WizardModal/WizardModal";
import { useAppContext } from "src/hooks/useAppContext";
import { usePixiWizardState } from "./hooks/usePixiWizardState";
import { usePixiWizardStep } from "./hooks/usePixiWizardStep";
import { useTrialistCurrency } from "./hooks/useTrialistCurrency";
import { consumePurchaseSuccess } from "./steps/CheckoutStep/purchaseResume";
import { CompleteStep } from "./steps/CompleteStep/CompleteStep";
import { EmailVerifyCard } from "./steps/PaymentShippingStep/components/EmailVerifyCard/EmailVerifyCard";
import { resolvePlanBillingFooter } from "./steps/PlanBillingStep/footer";
import {
  OnboardingPlansContext,
  useOnboardingPlans,
} from "./steps/PlanBillingStep/useOnboardingPlans";
import {
  PAYMENT_SHIPPING_STEP,
  PIXI_WIZARD_STEPS,
  PLAN_BILLING_STEP,
  STEP_COMPONENTS,
} from "./wizardSteps";
import {
  BackLabel,
  CurrencyGate,
  CurrencyGateError,
  PairScreenModalBody,
  WizardModalWidthOverride,
} from "./styles";

interface PixiWizardModalProps {
  orgId: string | undefined;
  onClose: () => void;
  /** True when the current user is already on Pro (Pro-trialist variant). */
  isProTrialist?: boolean;
}

export const PixiWizardModal = ({
  orgId,
  onClose,
  isProTrialist = false,
}: PixiWizardModalProps) => {
  const { step, goNext, goBack, editStep, isFirstStep, isLastStep } =
    usePixiWizardStep(orgId, PIXI_WIZARD_STEPS.length);
  const { state, updateState } = usePixiWizardState(orgId);
  const CurrentStep = STEP_COMPONENTS[step - 1];

  // Entering the Payment & Shipping step always re-mounts empty Stripe card
  // fields, so any card token / validity persisted from a prior visit is
  // stale. Clear both synchronously on every transition INTO the step (Back
  // from Checkout, Back-then-Forward via step 2, or a Checkout review pencil)
  // so Continue starts disabled with no enable frame — the card must be
  // re-entered. The token is deliberately NOT cleared on the way out: Checkout
  // (step 4) needs `cardPaymentMethodId` to charge the card.
  const resetStalePaymentOnEnter = useCallback(
    (nextStep: number) => {
      if (nextStep === PAYMENT_SHIPPING_STEP) {
        updateState({
          cardPaymentMethodId: undefined,
          paymentShippingValid: false,
        });
      }
    },
    [updateState],
  );

  const handleGoNext = useCallback(() => {
    resetStalePaymentOnEnter(step + 1);
    goNext();
  }, [resetStalePaymentOnEnter, step, goNext]);

  const handleGoBack = useCallback(() => {
    resetStalePaymentOnEnter(step - 1);
    goBack();
  }, [resetStalePaymentOnEnter, step, goBack]);

  const handleEditStep = useCallback(
    (target: number) => {
      resetStalePaymentOnEnter(target);
      editStep(target);
    },
    [resetStalePaymentOnEnter, editStep],
  );

  const context = useAppContext();
  const history = useHistory();

  // Email/password signups must verify their email before entering billing.
  // SSO/social users are provisioned `active`, so they never hit this gate and
  // reach the Payment & Shipping form directly. On success the verify card
  // re-syncs claims in place (no reload) so `isActiveUser` flips to true and the
  // form renders without losing wizard state (GRWBKK-1673).
  const needsEmailVerification =
    step === PAYMENT_SHIPPING_STEP && !context.isActiveUser;

  const onEmailVerified = async () => {
    await context.sessions.refreshAuthToken();
    await context.refetchUserStatus();
  };

  // The billing currency is the trialist's own (Chargebee locks it) — never a
  // user choice. Resolve it and seed the wizard so every step + checkout use it;
  // the currency isn't shown as a selector, only in the plan prices.
  const {
    currency: trialistCurrency,
    loading: currencyLoading,
    error: currencyError,
  } = useTrialistCurrency();

  useEffect(() => {
    if (trialistCurrency && state.currency !== trialistCurrency) {
      updateState({ currency: trialistCurrency });
    }
  }, [trialistCurrency, state.currency, updateState]);

  // A redirect purchase (PayPal/Direct Debit) that completed on the return trip
  // leaves a one-shot success flag; the billing page reopens the wizard so we
  // land on the Complete (confirmation) screen — the same screen the inline
  // card/invoice flow flips to. `purchased` is never persisted (PERSIST_OMIT),
  // so we derive it from the flag here. Consume-once so a later reopen returns
  // to Checkout rather than a sticky success screen.
  useEffect(() => {
    if (consumePurchaseSuccess()) {
      updateState({ purchased: true });
    }
  }, [updateState]);

  // Plan & Billing and Checkout price against the currency, so they must wait
  // for it — and block (never guess) if it can't be resolved. Payment & Shipping
  // (step 3) has no prices, so it doesn't gate.
  const needsCurrency =
    step === PLAN_BILLING_STEP || step === PIXI_WIZARD_STEPS.length;
  const currencyPending = needsCurrency && currencyLoading;
  const currencyFailed = needsCurrency && currencyError;

  // After a successful purchase the wizard shows the Complete (success) screen
  // in place of the step content, with the progress bar all complete and no
  // footer navigation (GRWBKK-1663).
  const isComplete = state.purchased === true;

  // The Complete button closes the wizard and sends the user to their screens.
  // A FULL page load is required after activation: the trial UI (subscribe
  // banner, welcome, onboarding) reads from several independent caches — the
  // AppContext subscription, the `useSubscription` cache, and the payment source
  // — that an in-place refetch can't all reset. Hard-navigate straight to a
  // fresh /screens in one step (`createHref` applies the router basename) rather
  // than a client-side push followed by a reload, which briefly flashes the
  // stale trial UI before reloading.
  const onCompletePurchase = () => {
    onClose();
    window.location.assign(history.createHref({ pathname: "/screens" }));
  };

  const onboardingPlans = useOnboardingPlans();

  // Plan & Billing owns its own footer total + Continue gating.
  const planFooter =
    step === PLAN_BILLING_STEP
      ? resolvePlanBillingFooter(onboardingPlans, state, isProTrialist)
      : undefined;

  // Payment & Shipping reports its validity up so Continue stays blocked until
  // the required payment, billing and shipping fields are valid (card
  // completeness is held by Stripe, so validity is reported from the step).
  // Require validity to be *explicitly* true: `undefined` (never reported yet,
  // e.g. the first render before the step's effect commits, or after a refresh
  // dropped the ephemeral flag) must block too, so there is no frame in which
  // Continue is clickable over empty card fields.
  const isPaymentInvalid =
    step === PAYMENT_SHIPPING_STEP && state.paymentShippingValid !== true;

  const isContinueBlocked =
    (planFooter ? !planFooter.canContinue : false) ||
    isPaymentInvalid ||
    needsEmailVerification;

  return (
    <OnboardingPlansContext.Provider
      value={{
        ...onboardingPlans,
        isProTrialist,
        orgCountryCode: context.currentOrg?.preferences.settings?.country_code,
      }}
    >
      <WizardModalWidthOverride />
      <WizardModal
        title={
          // On the success screen every step is done, so push currentStep past
          // the last step to render all four as filled (complete) circles.
          <StepProgress
            currentStep={isComplete ? PIXI_WIZARD_STEPS.length + 1 : step}
            totalSteps={PIXI_WIZARD_STEPS}
            compact
          />
        }
        onClose={onClose}
        footerStart={
          isComplete ? undefined : (planFooter?.footerStart ?? undefined)
        }
        primaryAction={
          // The Checkout step (last) owns its own Purchase button inside the
          // Order Summary card, so the footer shows no primary there — and the
          // Complete screen has its own Complete button.
          isLastStep || isComplete
            ? undefined
            : {
                label: (
                  <FormattedMessage
                    id="pixi_wizard.continue"
                    defaultMessage="Continue"
                  />
                ),
                onClick: handleGoNext,
                disabled: isContinueBlocked,
              }
        }
        secondaryAction={
          isFirstStep || isComplete
            ? undefined
            : {
                label: (
                  <BackLabel>
                    <Icon name="arrow-left" />
                    <FormattedMessage
                      id="pixi_wizard.back"
                      defaultMessage="Back"
                    />
                  </BackLabel>
                ),
                onClick: handleGoBack,
              }
        }
      >
        <PairScreenModalBody
          data-testid="pixi-wizard-modal-body"
          $flushVertical={step === PLAN_BILLING_STEP}
        >
          {isComplete ? (
            <CompleteStep state={state} onComplete={onCompletePurchase} />
          ) : needsEmailVerification ? (
            <EmailVerifyCard onVerified={onEmailVerified} />
          ) : currencyFailed ? (
            <CurrencyGate data-testid="currency-gate-error">
              <CurrencyGateError>
                <FormattedMessage
                  id="pixi_wizard.currency.error"
                  defaultMessage="We couldn't load your billing currency. Please close this and try again."
                />
              </CurrencyGateError>
            </CurrencyGate>
          ) : currencyPending ? (
            <CurrencyGate data-testid="currency-gate-loading">
              <Loader active inline size="large" />
            </CurrencyGate>
          ) : (
            <CurrentStep
              state={state}
              updateState={updateState}
              goToStep={handleEditStep}
            />
          )}
        </PairScreenModalBody>
      </WizardModal>
    </OnboardingPlansContext.Provider>
  );
};
